"use client";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Bounds, PerformanceMonitor, Stats } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import type { SideName } from "@tosize/shapes/src/lib/models/Side/SideName";
import type { ShapeInput } from "@tosize/shapes/src/types/shape";
import type { Workpiece } from "@tosize/shapes/src/types/workpiece";
import { type Dispatch, Fragment, type SetStateAction, Suspense, useEffect, useState } from "react";
import { IntlProvider, type MessageFormatElement, useIntl } from "react-intl";
import { LinearToneMapping } from "three";
import { type CanvasSettingType, CanvasSettings } from "./CanvasSettings";
import { usePanelVisualisation } from "./Context";
import { PanelVisualisationProvider } from "./Provider";
import { GenericShape } from "./canvas/generic-shape/Shape";
import { PresentationControls } from "./utils/PresentationControls";
import { SecretControls } from "./utils/SecretControls";
import { useDebug } from "./utils/debug";

export const MODIFIER = 1000;
export const DEFAULT_THICKNESS = 38;

export interface PanelVisuProps {
	productId?: string;
	workpiece: Workpiece;
	thickness?: number;
	grainType?: "lengthwise" | "broadwise";
	shape?: ShapeInput;
	finishInputs?: { category: string; code: SideName; name: string }[];
	currentField?: string;
	open: boolean;
	setOpen?: Dispatch<SetStateAction<boolean>>;
	view?: "2d" | "3d" | "detail";
	modus: "dimensions" | "finishes";
	variant?: "dialog" | "default";
}

export interface ExtendedVisuProps extends PanelVisuProps {
	lang: string;
	messages: Record<string, string> | Record<string, MessageFormatElement[]>;
	apiUrl: string;
	cartId?: string;
	apiToken?: string;
}

export default function Index(props: ExtendedVisuProps) {
	return (
		<IntlProvider locale={props.lang} messages={props.messages} onError={() => null}>
			<PanelVisualisationProvider {...props}>
				<Content {...props} />
			</PanelVisualisationProvider>
		</IntlProvider>
	);
}

function Content(props: ExtendedVisuProps) {
	const intl = useIntl();
	const { debugEnabled } = useDebug();
	const { shape, sidePaths, inputLines } = usePanelVisualisation();

	const [dpr, setDpr] = useState(2);
	const [canvasSettings, setCanvasSettings] = useState<CanvasSettingType>({
		view: props.view ?? "2d",
		field: props.currentField,
	});

	useEffect(() => {
		setCanvasSettings({ view: props.view ?? "2d", field: props.currentField });
	}, [props.currentField, props.view]);

	if (!shape) {
		return null;
	}

	return (
		<>
			{sidePaths && inputLines ? (
				<Fragment>
					<Canvas
						orthographic
						dpr={dpr}
						frameloop="demand"
						gl={{ sortObjects: false, toneMapping: LinearToneMapping }}
						className="touch-none"
					>
						<hemisphereLight />
						<directionalLight position={[0, 0, 5]} intensity={3} />

						<PresentationControls
							enabled={true} // the controls can be disabled by setting this to false
							speed={1.5} // Speed factor
							snap={true}
							rotation={[0, 0, 0]} // Default rotation
							polar={[(-Math.PI / 4) * 3, (Math.PI / 4) * 3]} // Vertical limits
							azimuth={[(-Math.PI / 4) * 3, (Math.PI / 4) * 3]} // Horizontal limits
						>
							<Bounds fit clip observe maxDuration={2}>
								<GenericShape
									modus={props.modus}
									finishInputs={props.finishInputs}
									grainType={props.grainType}
									canvasSettings={canvasSettings}
								/>
							</Bounds>
						</PresentationControls>

						{debugEnabled && <Stats />}

						<PerformanceMonitor onChange={({ factor }) => setDpr(Math.round(0.5 + 1.5 * factor))} />

						<Suspense>
							<SecretControls />
						</Suspense>
					</Canvas>

					{debugEnabled && (
						<div className="absolute top-2 left-2">
							<div className="rounded-xl bg-danger px-2 py-px font-bold text-white text-xs">
								Debug enabled
							</div>
						</div>
					)}

					{props.variant !== "dialog" && (
						<button
							type="button"
							className="absolute top-0 right-0 flex h-8 w-8 shrink-0 appearance-none items-center justify-center rounded-bl bg-shades-light enter:bg-shades p-1 text-xl focus:outline-none motion-safe:transition sm:p-2"
							onClick={() => props.setOpen?.((prev) => !prev)}
							title={intl.formatMessage({ defaultMessage: "Sluiten" })}
						>
							<FontAwesomeIcon icon={faTimes} fixedWidth />
						</button>
					)}
					<CanvasSettings canvasSettings={canvasSettings} setCanvasSettings={setCanvasSettings} />
				</Fragment>
			) : (
				<Fallback {...props} />
			)}
		</>
	);
}

function Fallback(props: Pick<PanelVisuProps, "workpiece" | "shape" | "modus">) {
	const { workpiece, shape, modus } = props;

	return (
		<img
			src={modus === "finishes" ? shape?.images.side_names : shape?.images.dimensions}
			alt={workpiece?.shape}
			width={600}
			height={301}
		/>
	);
}
